<script setup lang="ts">
import gsap from 'gsap'
import { A11y, EffectFade } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import 'swiper/css'
import 'swiper/css/effect-fade'

const modules = [A11y, EffectFade]

const props = defineProps<{
  data: SectionNumbersBlockRecord
  moduleIndex: number
}>()

const { level, isSemantic, invertedStyles } = useSectionRules(
  props.moduleIndex,
  props.data.invertColorTheme,
  !!props.data.title
)

const titleHTML = computed(() => {
  return (props.data.title ?? '')
    .trim()
    .split(' ')
    .map(
      (word, idx) =>
        `<div class="flex overflow-hidden items-end gap-4">
          <span class="fx-t1 inline-flex text-neutral-700">${word}</span>
          <span class="fx-t2 font-normal inline-flex">${props.data?.blocks?.[idx]?.value ?? ''}</span>
          <span class="text-lg font-medium tracking-normal uppercase inline-flex pb-1 overflow-hidden"><span class="fx-t3 inline-block">${props.data?.blocks?.[idx]?.text ?? ''}</span></span>
        </div>`
    )
    .join(' ')
})

const testimonialsHTML = computed(() => {
  return (props.data.titleTestimonials ?? '')
    .trim()
    .split(' ')
    .map((word) => `<div class="overflow-hidden"><div class="fx-tc">${word}</div></div>`)
    .join(' ')
})

const ctxRef = ref<HTMLElement | null>(null)
const { height } = useElementSize(ctxRef)
let ctx: gsap.Context | null = null
let tl: GSAPTimeline | null = null
const activeTestimonialIdx = ref<number | null>(0)

const swiperI = ref<typeof Swiper | null>(null)

const onSwiper = (swiper: unknown) => {
  swiperI.value = swiper
}

onMounted(() => {
  if (ctxRef.value) {
    gsap.registerPlugin(ScrollTrigger)

    const mm = gsap.matchMedia()
    mm.add('(min-width: 1024px)', () => {
      ctx = gsap.context((self: gsap.Context) => {
        const elPin = self.selector ? self.selector('.fx-numbers-pin') : null
        const titleNumbers = self.selector ? self.selector('.fx-t1') : null
        const txtNumbers = self.selector ? self.selector('.fx-t2') : null
        const txtLabels = self.selector ? self.selector('.fx-t3') : null
        const titleClients = self.selector ? self.selector('.fx-tc') : null
        const elTestimonials = self.selector ? self.selector('.fx-testimonials') : null

        // Pinning behavior removed for desktop
        ScrollTrigger.create({
          trigger: elPin,
          start: 'center center',
          end: `+=${height.value}`,
          markers: false
        })

        gsap.set([titleNumbers, txtNumbers, titleClients], { y: '100%' }) // Set initial position
        gsap.set([txtLabels], { autoAlpha: 0 })
        gsap.set(elTestimonials, { y: '20%', autoAlpha: 0 })

        tl = gsap.timeline({
          scrollTrigger: {
            trigger: elPin,
            scrub: true,
            start: 'top 95%',
            end: `+=${height.value}`,
            markers: false,
            toggleActions: 'play pause reverse reset'
          }
        })

        // Animate titleNumbers into view
        tl.to(titleNumbers, {
          duration: 0.5,
          y: '0%' // Bring titleNumbers into view
        })

        tl.to(
          txtNumbers,
          {
            duration: 0.5,
            y: '0%'
          },
          '-=0.2'
        )

        tl.to(
          txtLabels,
          {
            duration: 0.5,
            autoAlpha: 1
          },
          '-=0.2'
        )

        tl.addLabel('numsOut')

        // Animate titleNumbers out of view
        tl.to(
          titleNumbers,
          {
            duration: 0.5,
            y: '-100%'
          },
          'numsOut'
        )

        tl.to(
          txtLabels,
          {
            duration: 0.5,
            autoAlpha: 0
          },
          'numsOut-=0.1'
        )

        tl.to(
          txtNumbers,
          {
            duration: 1,
            y: '-100%'
          },
          'numsOut'
        )

        tl.addLabel('clients', '-=2')

        tl.to(
          titleClients,
          {
            duration: 0.5,
            y: 0
          },
          'clients'
        )

        tl.to(
          elTestimonials,
          {
            autoAlpha: 1,
            duration: 0.5,
            y: 0
          },
          'clients+=0.1'
        )

        ScrollTrigger.refresh()
      }, ctxRef.value as HTMLElement)
    })
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})

function increaseActiveTestimonial() {
  swiperI.value?.slideNext()
}
</script>

<template>
  <div ref="ctxRef" class="container overflow-hidden lg:pb-80" :class="invertedStyles">
    <DeliciousSausage class="absolute right-0 top-0 translate-x-1/3" name="numbers-top" />
    <!-- Too much sausage
    <DeliciousSausage class="absolute right-0 top-1/2 translate-x-1/4" name="numbers-bot" />
    -->
    <DeliciousSausage
      class="absolute bottom-0 left-0 -translate-x-1/4 -rotate-45"
      name="numbers-clients-top"
    />

    <div class="fx-numbers-pin relative">
      <div class="mb-28 lg:mb-0">
        <div
          v-if="data.title"
          :level="level"
          :size="undefined"
          :is-semantic="false"
          class="mb-12 hidden text-6xl-bl leading-[0.8] lg:mb-80 lg:block lg:text-9xl-bl"
        >
          <div v-html="titleHTML" />
        </div>
        <Heading
          v-if="data.title"
          :level="level"
          :size="undefined"
          :is-semantic="isSemantic"
          class="mb-6 text-6xl-bl text-neutral-700 lg:hidden"
          ><TextReveal :text="data.title"
        /></Heading>
        <div class="flex flex-col gap-2 lg:hidden">
          <div v-for="(item, idx) in data.blocks" :key="idx" class="flex flex-col gap-1">
            <div v-if="item.value" class="text-6xl">
              <TextReveal :text="item.value"></TextReveal>
            </div>
            <div v-if="item.text" class="font-medium">
              <TextReveal :text="item.text"></TextReveal>
            </div>
          </div>
        </div>
      </div>
      <div class="w-full lg:left-0 lg:top-0">
        <Heading
          v-if="data.titleTestimonials"
          :level="level"
          :size="undefined"
          :is-semantic="true"
          class="mb-12 text-6xl-bl text-neutral-700 lg:mb-24 lg:text-9xl-bl"
          ><TextReveal :text="testimonialsHTML" />
        </Heading>
        <div class="fx-testimonials relative">
          <Swiper
            :modules="modules"
            :slides-per-view="1"
            :space-between="0"
            :loop="true"
            :speed="800"
            :allow-touch-move="false"
            :effect="'fade'"
            :fade-effect="{
              crossFade: true
            }"
            @swiper="onSwiper"
          >
            <SwiperSlide v-for="(item, idx) in data.testimonials" :key="idx">
              <TestimonialItem
                :key="idx"
                :data="item"
                layout="row"
                :is-scrubbed="idx === 0"
                :is-visible="activeTestimonialIdx === idx"
              />
            </SwiperSlide>
          </Swiper>
          <Button
            v-if="activeTestimonialIdx !== null"
            variant="outline"
            aria-label="Next testimonial"
            class="z-[9] mt-8 flex w-11 gap-4 !px-0 lg:!absolute lg:left-[116px] lg:top-32"
            :hover-effect="false"
            @click="increaseActiveTestimonial"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.293 17.293L12.707 18.707L19.414 12L12.707 5.29297L11.293 6.70697L15.586 11H6V13H15.586L11.293 17.293Z"
                fill="black"
              />
            </svg>
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
